import React, { useEffect, useState } from 'react';
import {
  Box,
  Container,
  Typography,
  Card,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  useTheme,
  alpha,
  Stack,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  CircularProgress,
  Avatar,
  Grid,
  IconButton,
  Tooltip,
} from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import { fetchCommitteeMembers, createCommitteeMember, deleteCommitteeMember } from '../store/slices/committeeMembersSlice';
import { Add as AddIcon, Delete as DeleteIcon } from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import SingleFileUpload from '../components/fileUpload/FileUpload';
import { getFileLink } from '../utils/getFileLink';

const CommitteeMembersPage = () => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const { enqueueSnackbar } = useSnackbar();
  
  const { members, loading, error } = useSelector((state) => state.committeeMembers);
  const { adminInfo } = useSelector((state) => state.auth);

  const [openDialog, setOpenDialog] = useState(false);
  const [newMember, setNewMember] = useState({
    name: '',
    email: '',
    biography: '',
    imagePath: '',
    signaturePath: '',
  });

  useEffect(() => {
    if (adminInfo?.type === 'SUPER_ADMIN') {
      dispatch(fetchCommitteeMembers());
    }
  }, [dispatch, adminInfo]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setNewMember(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async () => {
    try {
      await dispatch(createCommitteeMember(newMember)).unwrap();
      enqueueSnackbar('Komite üyesi başarıyla eklendi', { variant: 'success' });
      setOpenDialog(false);
      setNewMember({
        name: '',
        email: '',
        biography: '',
        imagePath: '',
        signaturePath: '',
      });
    } catch (error) {
      enqueueSnackbar('Komite üyesi eklenirken bir hata oluştu', { variant: 'error' });
    }
  };

  const handleDelete = async (committeeMemberId) => {
    if (window.confirm('Bu komite üyesini silmek istediğinizden emin misiniz?')) {
      try {
        await dispatch(deleteCommitteeMember(committeeMemberId)).unwrap();
        enqueueSnackbar('Komite üyesi başarıyla silindi', { variant: 'success' });
      } catch (error) {
        enqueueSnackbar('Komite üyesi silinirken bir hata oluştu', { variant: 'error' });
      }
    }
  };

  if (adminInfo?.type !== 'SUPER_ADMIN') {
    return (
      <Container maxWidth="lg">
        <Box sx={{ mt: 4 }}>
          <Typography variant="h4" color="error">
            Bu sayfaya erişim yetkiniz bulunmamaktadır.
          </Typography>
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg">
      <Box sx={{ mt: 4 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center" mb={4}>
          <Typography variant="h4" gutterBottom>
            Komite Üyeleri Yönetimi
          </Typography>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setOpenDialog(true)}
            sx={{
              bgcolor: theme.palette.primary.main,
              color: 'white',
              '&:hover': {
                bgcolor: theme.palette.primary.dark,
              },
            }}
          >
            Yeni Komite Üyesi Ekle
          </Button>
        </Stack>

        <Card sx={{ borderRadius: 2 }}>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Üye</TableCell>
                  <TableCell>E-posta</TableCell>
                  <TableCell>Biyografi</TableCell>
                  <TableCell>İmza</TableCell>
                  <TableCell>Kayıt Tarihi</TableCell>
                  <TableCell>İşlemler</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={6} align="center">
                      <CircularProgress />
                    </TableCell>
                  </TableRow>
                ) : members.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} align="center">
                      <Typography color="text.secondary">
                        Henüz komite üyesi bulunmamaktadır.
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  members.map((member) => (
                    <TableRow key={member.committeeMemberId}>
                      <TableCell>
                        <Stack direction="row" alignItems="center" spacing={2}>
                          <Avatar 
                            src={getFileLink(member.imagePath)} 
                            alt={member.name}
                            sx={{ 
                              width: 48, 
                              height: 48,
                              cursor: 'pointer',
                              '&:hover': {
                                opacity: 0.8,
                              }
                            }}
                            onClick={() => window.open(getFileLink(member.imagePath), '_blank')}
                          >
                            {member.name.charAt(0)}
                          </Avatar>
                          <Typography>{member.name}</Typography>
                        </Stack>
                      </TableCell>
                      <TableCell>{member.email}</TableCell>
                      <TableCell>{member.biography}</TableCell>
                      <TableCell>
                        {member.signaturePath && (
                          <Box
                            component="img"
                            src={getFileLink(member.signaturePath)}
                            alt="İmza"
                            sx={{
                              width: 100,
                              height: 'auto',
                              cursor: 'pointer',
                              '&:hover': {
                                opacity: 0.8,
                              }
                            }}
                            onClick={() => window.open(getFileLink(member.signaturePath), '_blank')}
                          />
                        )}
                      </TableCell>
                      <TableCell>
                        {new Date(parseInt(member.registerTime)).toLocaleDateString('tr-TR')}
                      </TableCell>
                      <TableCell align="right">
                        <Tooltip title="Sil">
                          <IconButton
                            onClick={() => handleDelete(member.committeeMemberId)}
                            sx={{
                              color: theme.palette.error.main,
                              '&:hover': {
                                bgcolor: alpha(theme.palette.error.main, 0.1),
                              },
                            }}
                          >
                            <DeleteIcon />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Card>
      </Box>

      <Dialog open={openDialog} onClose={() => setOpenDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>Yeni Komite Üyesi Ekle</DialogTitle>
        <DialogContent>
          <Grid container spacing={3} sx={{ mt: 1 }}>
            <Grid item xs={12} md={6}>
              <Stack spacing={3}>
                <TextField
                  fullWidth
                  label="Ad Soyad"
                  name="name"
                  value={newMember.name}
                  onChange={handleInputChange}
                />
                <TextField
                  fullWidth
                  label="E-posta"
                  name="email"
                  type="email"
                  value={newMember.email}
                  onChange={handleInputChange}
                />
                <TextField
                  fullWidth
                  label="Biyografi"
                  name="biography"
                  multiline
                  rows={4}
                  value={newMember.biography}
                  onChange={handleInputChange}
                />
              </Stack>
            </Grid>
            <Grid item xs={12} md={6}>
              <Stack spacing={3}>
                <Box>
                  <Typography variant="subtitle1" gutterBottom>
                    Profil Fotoğrafı
                  </Typography>
                  <SingleFileUpload
                    onUploadComplete={(path) => setNewMember(prev => ({ ...prev, imagePath: path }))}
                    fileUploadPath="committee-members"
                  />
                </Box>
                <Box>
                  <Typography variant="subtitle1" gutterBottom>
                    İmza
                  </Typography>
                  <SingleFileUpload
                    onUploadComplete={(path) => setNewMember(prev => ({ ...prev, signaturePath: path }))}
                    fileUploadPath="committee-members"
                  />
                </Box>
              </Stack>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDialog(false)}>İptal</Button>
          <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={!newMember.name || !newMember.email || !newMember.imagePath || !newMember.signaturePath}
          >
            Ekle
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default CommitteeMembersPage; 